import * as React from 'react';
import { Dispatch } from './types';

/**
 * Scroll given element into view
 * @param element
 */
export const scrollIntoView = (element: HTMLElement) => {
  if (element) {
    window.scrollTo({
      // need to offset the height of the portal menubar
      top: element.offsetTop - 100,
    });
  }
};

/**
 * Checks if given element is in viewport
 * https://stackoverflow.com/a/7557433
 * @param element
 * @returns boolean
 */
export const isElementInViewport = (element: HTMLElement): boolean => {
  const rect = element.getBoundingClientRect();

  if (rect.height > window.innerHeight) {
    const inViewButBiggerThanWindowHeight =
      rect.top >= 0 &&
      rect.top < 125 &&
      rect.left >= 0 &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth);
    if (inViewButBiggerThanWindowHeight) {
      return true;
    }
  }

  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <=
      (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
};

/**
 * Get the delta between two array of strings
 * @param original
 * @param current
 * @returns the delta and if its add or remove
 */
export const getDelta = (original: string[], current: string[]) => {
  let delta: string[] = [];
  let isAdd = true;
  if (current.length > original.length) {
    // If a item has been added
    delta = current.filter((value) => !original.includes(value));
  } else if (current.length < original.length) {
    // if a item(s) has been removed
    isAdd = false;
    delta = original.filter(
      (value) => current.filter((value2) => value === value2).length < 1,
    );
  }
  // if there is no difference then delta will be empty
  return { delta, isAdd };
};
/**
 * Returns the plural or singular noun with its count. Only works for nouns that the plural form ends with "s"
 * @param count
 * @param noun
 * @param noCountWord
 */
export const getPluralOrSingularNoun = (
  count: number,
  noun: string,
  noCountWord?: string,
): string => {
  if (count === 1) {
    return count + ' ' + noun;
  } else if (count > 1) {
    return count + ' ' + noun + 's';
  } else {
    return noCountWord || '';
  }
};

/**
 * For Onboarding Accordion Section: returns a title for the given state of the section
 * @param isCompleted
 * @param expandedItems
 * @param accordionKey
 * @param completeText
 * @param incompleteText
 */
export const getAccordionHeader = (
  isCompleted: boolean,
  expandedItems: string[],
  accordionKey: string,
  completeText: string,
  incompleteText: string,
) => {
  if (!expandedItems.includes(accordionKey)) {
    return isCompleted ? completeText : incompleteText;
  } else {
    return accordionKey;
  }
};

export const getDisplayName = (C: React.ComponentType<any>) =>
  C.displayName || C.name || 'Component';

// TypeScript utils
export type OptionalExceptFor<T, TRequired extends keyof T> = Partial<T> &
  Pick<T, TRequired>;

export const devAuth0Login = async (username: string | null) => {
  const sign = require('jwt-encode');

  const tomorrow = new Date();
  tomorrow.setDate(new Date().getDate() + 1);

  const response = await fetch('/api/v1/dev-secret-key');
  const secret = (await response.json()).secret;

  const tokenData = {
    iat: Math.round(Date.now() / 1000),
    exp: Math.round(tomorrow.getTime() / 1000),
    sub: username,
    iss: 'https://auth.arcticwolf.com/',
    aud: 'https://api.arcticwolf.com/',
    CUSTOM_EMAIL_CLAIM: username,
  };

  const primaryJWT = sign(tokenData, secret);

  const data = {
    body: {
      access_token: primaryJWT,
      expires_in: 86400,
      decodedToken: {
        user: {
          name: username,
        },
      },
    },
    expiresAt: tomorrow.valueOf(),
  };

  const audience = process.env.REACT_APP_AUTH0_AUDIENCE;
  const clientID = process.env.REACT_APP_AUTH0_CLIENT_ID;
  window.localStorage.setItem(
    `@@auth0spajs@@::${clientID}::${audience}::openid profile email offline_access`,
    JSON.stringify(data),
  );
};

export const chainApiDispatches = async (
  dispatch: Dispatch,
  apiCalls: ((dispatch: Dispatch) => Promise<any>)[],
) => {
  for (let api of apiCalls) {
    try {
      await dispatch(api);
    } catch (e) {
      // ignore as it is handled in the api request
    }
  }
};

export const containsObject = (list: any[], object: any) => {
  for (const i in list) {
    if (JSON.stringify(list[i]) === JSON.stringify(object)) {
      return true;
    }
  }
  return false;
};
